/**
 * Bot WebApp → Algomed backend.
 * Backend her isteği initData hash'i ile doğrular (telegramAuthMiddleware).
 */
import { getInitData, refreshInitData } from '../telegram';

const API_URL = import.meta.env.VITE_ALGOMED_API_URL || 'https://rmikrobiyoloji-production-ffd0.up.railway.app';

/**
 * Standart error body — backend tum 4xx/5xx response'larinda dondurur.
 * Caller'lar `err.body.code` ve `err.body.usage` uzerinden zengin UI uretebilir.
 */
export interface ApiErrorBody {
  message?: string;
  code?: 'FREE_PLAN_LIMIT_EXCEEDED' | 'TIER_LOCKED' | 'SRS_STALE' | string;
  // Limit ihlalinde dolu gelen alanlar
  used?: number;
  limit?: number | null;
  resetAt?: string;
  // Diger backend alanlari (genis tut)
  [k: string]: unknown;
}

export interface ApiError extends Error {
  status: number;
  code?: string;
  body: ApiErrorBody | null;
}

async function call<T>(path: string, init?: RequestInit, opts: { _retried?: boolean } = {}): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, {
    ...init,
    headers: {
      'content-type': 'application/json',
      'x-telegram-init-data': getInitData(),
      ...(init?.headers || {}),
    },
  });
  if (!res.ok) {
    // 401: initData stale olmus olabilir → tek seferlik retry sonrasi yenilen.
    if (res.status === 401 && !opts._retried) {
      try { refreshInitData(); } catch { /* sessiz */ }
      return call<T>(path, init, { _retried: true });
    }
    // Body'yi parse et — caller `err.body.code/used/limit/resetAt` uzerinden
    // zengin UI uretsin diye.
    let body: ApiErrorBody | null = null;
    try { body = await res.json() as ApiErrorBody; }
    catch { /* gov dose / network */ }
    const err = Object.assign(
      new Error(`API ${res.status}: ${body?.message || res.statusText}`),
      { status: res.status, body, code: body?.code },
    ) as ApiError;
    throw err;
  }
  return res.json() as Promise<T>;
}

// ──────────────────────────────────────────────────────────
// Tip tanımları (worker/algomed-client.ts ile uyumlu)
// ──────────────────────────────────────────────────────────

export interface SrsSummaryResp {
  total: number;
  due: number;
  mastered: number;
  byBox: { box: number; count: number }[];
}

export interface SrsListItem {
  id: number;
  boxNumber: number;
  isMastered: boolean;
  nextReviewAt: string;
  lastReviewedAt: string | null;
  kind: 'question' | 'flashcard' | 'topic';
  itemId: number;
  title: string;
}

export type SrsKind = 'question' | 'flashcard' | 'topic';

export interface SrsNextResp {
  kind: SrsKind;
  srsId: number;
  boxNumber: number;
  /**
   * Optimistic concurrency token — `lastReviewedAt` snapshot ('NEW' veya
   * ISO date string). srsReview cagrisinda body'de geri gonderilmeli;
   * yoksa baska bir cihaz item'i ileriye almissa kota iki kez harcanabilir.
   */
  claimToken: string;
  item: any;
}

export interface SrsReviewResp {
  ok: true;
  boxNumber: number;
  isMastered: boolean;
  nextReviewAt: string;
}

export interface MentorSummaryResp {
  tier: string;
  mentorAvailable: boolean;
  accessLevel: 'lite' | 'limited' | 'full';
  stats: {
    totalAttempts: number;
    correctAttempts: number;
    accuracy: number;
    last7days: number;
    streak: number;
  };
  srs: { box: number; due: number };
  weakTopics: { id: number; name: string; accuracy: number; attempts: number }[];
}

export interface MentorCoachTip {
  icon: string;
  text: string;
  action: string;
}

export interface MentorCoachResp {
  tips: MentorCoachTip[];
  todayCount: number;
  yesterdayCount: number;
  srsDue: number;
}

export interface MentorStudyPatternResp {
  days: { date: string; weekday: number; count: number; correct: number }[];
  hours: { hour: number; count: number }[];
  total14: number;
  avgPerDay: number;
  peakDay: { date: string; count: number };
  peakHour: { hour: number; count: number };
}

export interface MentorForgettingResp {
  engine: string;
  overdue: { srsId: number; boxNumber: number; nextReviewAt: string; kind: string; title: string }[];
  upcoming48h?: number;
  model?: any;
}

export interface MentorExamReadinessResp {
  mode: 'locked' | 'preview' | 'full';
  message?: string;
  score?: number;
  confidence?: 'low' | 'medium' | 'high';
  attemptCount?: number;
  engine?: string;
  [k: string]: any;
}

export interface MentorPeerCompareResp {
  available: boolean;
  message?: string;
  myAccuracy?: number;
  peerAccuracy?: number;
  myAttempts?: number;
  delta?: number;
  engine?: string;
}

export interface MentorBurnoutResp {
  available: boolean;
  message?: string;
  signal?: 'normal' | 'drop' | 'accuracy_drop' | 'overload';
  recent?: { count: number; accuracy: number };
  prior?: { count: number; accuracy: number };
}

export interface ExamClassificationItem {
  id: number;
  name: string;
  tier: string;
  accessible?: boolean;
}

export interface BranchItem {
  id: number;
  name: string;
  examClassificationId: number;
  accessible?: boolean;
}

export interface TopicItem {
  id: number;
  name: string;
  parentId: number | null;
  branchId: number | null;
  examClassificationId: number | null;
  accessible?: boolean;
}

export interface LectureMetaItem {
  id: number;
  title: string;
  topicId: number;
}

/**
 * Backend `/api/telegram/webapp/me` ve `/api/telegram/me` response shape'i.
 * Tek kanonik tip — tum sayfalarda buradan import edilmeli.
 */
export interface MeResp {
  linked: boolean;
  shadow: boolean;
  algomedUserId?: number;
  tier: 'free' | 'silver' | 'gold' | 'diamond';
  isAdmin: boolean;
  /** Aktif Subscription'in bitis tarihi (ISO). Plansiz/free'de null. */
  subscriptionEndsAt: string | null;
  displayName: string | null;
}

/**
 * `redeemCode` response'u — shadow user'in target'a aktarilmasinda
 * `merge` field'i doldurulur; bot Mini App linking flow'unun ardindan
 * "X tablo aktarildi" toast/celebration gostermek icin kullanilir.
 */
export interface RedeemCodeResp {
  algomedUserId: number;
  tier: string;
  merge?: {
    tablesAffected: Record<string, number>;
    subscriptionsMerged: number;
  };
}

// ──────────────────────────────────────────────────────────
// API surface
// ──────────────────────────────────────────────────────────

export const api = {
  startLink: () => call<{ code: string; expiresAt: string }>('/api/telegram/webapp/link/start', {
    method: 'POST', body: JSON.stringify({}),
  }),

  topics: () => call<Array<{ id: number; name: string; parentId: number | null }>>('/api/telegram/content/topics'),

  randomQuestion: (topicId?: number) =>
    call<any>(`/api/telegram/content/question/random${topicId ? `?topicId=${topicId}` : ''}`),

  randomFlashcard: (topicId?: number) =>
    call<any>(`/api/telegram/content/flashcard/random${topicId ? `?topicId=${topicId}` : ''}`),

  randomAlgospot: (topicId?: number) =>
    call<any>(`/api/telegram/content/algospot/random${topicId ? `?topicId=${topicId}` : ''}`),

  lecture: (id: number) => call<any>(`/api/telegram/content/lecture/${id}`),

  getQuestion: (id: number) => call<any>(`/api/telegram/content/question/${id}`),
  getFlashcard: (id: number) => call<any>(`/api/telegram/content/flashcard/${id}`),
  getAlgospot: (id: number) => call<any>(`/api/telegram/content/algospot/${id}`),

  /**
   * Soru cevabini Algomed backend'ine kaydet — QuestionAttempt'e yazilir.
   * (Audit Content-C1) Bot/Mini App kullanicilarinin mentor analytics,
   * peer compare ve stats sayaclari icin sart.
   * Fire-and-forget cagrilmali; 401/403 hata durumunda UI akisi etkilenmez.
   */
  recordAttempt: (body: {
    questionId: number;
    selectedAnswer: string;
    isCorrect: boolean;
    timeTaken?: number | null;
  }) =>
    call<{ id: number; isCorrect: boolean; selectedAnswer: string; createdAt: string }>(
      '/api/telegram/attempts',
      { method: 'POST', body: JSON.stringify(body) },
    ),

  usage: () => call<Array<{ feature: string; used: number; limit: number | null; resetAt: string }>>(
    '/api/telegram/webapp/usage'
  ),

  me: () => call<MeResp>('/api/telegram/webapp/me'),

  // ── SRS / Dijital Antrenör ───────────────────────────────
  srsSummary: () => call<SrsSummaryResp>('/api/telegram/srs/summary'),
  srsList: (opts?: { offset?: number; limit?: number }) => {
    const q = new URLSearchParams();
    if (opts?.offset != null) q.set('offset', String(opts.offset));
    if (opts?.limit != null) q.set('limit', String(opts.limit));
    const qs = q.toString();
    return call<SrsListItem[]>(`/api/telegram/srs/list${qs ? `?${qs}` : ''}`);
  },
  srsNext: () => call<SrsNextResp>('/api/telegram/srs/next'),
  /**
   * SRS tekrarini kaydet. `claimToken` srsNext response'undan alinmali —
   * race condition koruma icin gereklidir. Yoksa backend "loose mode"a duser
   * (ileride zorunlu olabilir).
   */
  srsReview: (srsId: number, correct: boolean, claimToken?: string) =>
    call<SrsReviewResp>('/api/telegram/srs/review', {
      method: 'POST',
      body: JSON.stringify({ srsId, correct, claimToken }),
    }),
  srsRemove: (srsId: number) =>
    call<{ ok: boolean }>('/api/telegram/srs/remove', {
      method: 'POST',
      body: JSON.stringify({ srsId }),
    }),
  srsAdd: (body: { questionId?: number; flashCardId?: number; topicId?: number }) =>
    call<{ ok: true; alreadyExisted?: boolean }>('/api/telegram/srs/add', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // ── Mentor V2 ─────────────────────────────────────────────
  mentorSummary: () => call<MentorSummaryResp>('/api/telegram/mentor/summary'),
  mentorCoach: () => call<MentorCoachResp>('/api/telegram/mentor/coach'),
  mentorStudyPattern: () => call<MentorStudyPatternResp>('/api/telegram/mentor/study-pattern'),
  mentorForgetting: () => call<MentorForgettingResp>('/api/telegram/mentor/forgetting'),
  mentorExamReadiness: () => call<MentorExamReadinessResp>('/api/telegram/mentor/exam-readiness'),
  mentorPeerCompare: () => call<MentorPeerCompareResp>('/api/telegram/mentor/peer-compare'),
  mentorBurnout: () => call<MentorBurnoutResp>('/api/telegram/mentor/burnout'),

  // ── Konu Evreni hiyerarşisi ───────────────────────────────
  examClassifications: () =>
    call<ExamClassificationItem[]>('/api/telegram/content/exam-classifications'),
  branches: (examClassificationId?: number) => {
    const qs = examClassificationId
      ? `?examClassificationId=${examClassificationId}`
      : '';
    return call<BranchItem[]>(`/api/telegram/content/branches${qs}`);
  },
  topicsList: (params?: { branchId?: number; examClassificationId?: number; parentId?: number | null }) => {
    const q = new URLSearchParams();
    if (params?.branchId) q.set('branchId', String(params.branchId));
    if (params?.examClassificationId) q.set('examClassificationId', String(params.examClassificationId));
    if (params && 'parentId' in params) {
      q.set('parentId', params.parentId == null ? 'null' : String(params.parentId));
    }
    const qs = q.toString();
    return call<TopicItem[]>(`/api/telegram/content/topics${qs ? `?${qs}` : ''}`);
  },
  lecturesByTopic: (topicId: number) =>
    call<LectureMetaItem[]>(`/api/telegram/content/lectures?topicId=${topicId}`),
};
